import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLiveChannel } from './useLiveChannel';
import { countsForAttention } from '../lib/partner-attention';

const API = '/api';

export interface PartnerCounts {
  inbox: number;
  feedback: number;
  /** Ungelesene Beitraege anderer im Gruppenraum (Server: partner-group.ts, Gelesen-Stand je Person).
   *  Bewusst NICHT in `total`: der Gruppenraum hat sein eigenes rotes Signal im Kopf (GruppenraumKnopf). */
  gruppe: number;
  total: number;
}

/**
 * Shared "offene Partner-Items"-Zähler für Inbox / Feedback.
 * Zählt exakt wie der ToolHub-Badge: ungelesene Nachrichten + neue Feedbacks.
 * 30s-Poll + Live-Update via WS (jedes partner-* Event). Gedacht für globale
 * Surfacing-Anzeige UND ToolHub-Badges (eine Quelle). Tasks wurden 2026-07
 * aus dem Menü genommen (nachweislich unbenutzt) und zählen nicht mehr.
 */
export function usePartnerCounts(): PartnerCounts {
  const { user, canAccessPanel } = useAuth();
  const gruppenraum = canAccessPanel('group-chat');
  const userId = user?.id ?? 'admin';
  const isAdmin = !user || user.role === 'admin' || user.role === 'product-owner';
  const [counts, setCounts] = useState<PartnerCounts>({ inbox: 0, feedback: 0, gruppe: 0, total: 0 });

  // Nach dem Unmount nicht mehr in den State schreiben — die Abfragen laufen
  // asynchron weiter und wuerden sonst auf einer toten Komponente landen.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const recompute = useCallback(async () => {
    let inbox = 0, feedback = 0, gruppe = 0;
    // Inbox: ungelesene Nachrichten von anderen (nicht eigene Drafts)
    try {
      const r = await fetch(`${API}/partner/messages?userId=${encodeURIComponent(userId)}`);
      if (r.ok) {
        const data = await r.json();
        // `needsAttention` kommt fertig vom Server (attentionUnreadIds in
        // partner-messages.ts). Frueher rechnete dieser Zaehler selbst — und
        // zwar loser als Abzeichen und Panel: er zaehlte Monitoring-Meldungen
        // und gefaltete Alarme mit, die der "alles als gelesen"-Knopf nie
        // anfasst. Ergebnis war ein Banner, das die 0 strukturell nicht
        // erreichen konnte (gemessen 31.08.2026: Banner ~85, Abzeichen 32).
        const msgs = (data.messages ?? []) as Array<Parameters<typeof countsForAttention>[0]>;
        inbox = msgs.filter(m => countsForAttention(m, userId)).length;
      }
    } catch { /* silent-ok: best-effort surfacing count */ }
    // Feedback: 'new' (Admin sieht alle, Partner eigene)
    try {
      const r = await fetch(`${API}/partner/feedback${isAdmin ? '' : `?userId=${encodeURIComponent(userId)}`}`);
      if (r.ok) {
        const arr = await r.json() as Array<{ status: string }>;
        feedback = Array.isArray(arr) ? arr.filter(e => e.status === 'new').length : 0;
      }
    } catch { /* silent-ok */ }
    // Gruppenraum: nur wer ihn sehen darf, fragt danach (wie customer-comms in ToolHub).
    if (gruppenraum) {
      try {
        const r = await fetch(`${API}/partner/group-messages?userId=${encodeURIComponent(userId)}`); // trust-boundary:ok eigene Route; gelesen wird nur ungelesen, unten per typeof number geprüft
        if (r.ok) {
          const data = await r.json() as { ungelesen?: unknown };
          gruppe = typeof data.ungelesen === 'number' ? data.ungelesen : 0;
        }
      } catch { /* silent-ok: best-effort surfacing count */ }
    }
    if (aliveRef.current) setCounts({ inbox, feedback, gruppe, total: inbox + feedback });
  }, [userId, isAdmin, gruppenraum]);

  // Der 30s-Takt bleibt die Grundsicherung: Er ist der Grund, warum dieser
  // Zaehler am 15.08.2026 als einziger richtig lag, waehrend die Panels an
  // einer toten Verbindung einfroren. Die Live-Ereignisse machen ihn nur
  // schneller — sie sind nicht die Quelle der Wahrheit.
  useEffect(() => {
    recompute();
    const interval = setInterval(recompute, 30000);
    return () => clearInterval(interval);
  }, [recompute]);

  // Der Gruppenraum hat gerade „gelesen" gemeldet: sofort neu rechnen, nicht erst im 30s-Takt.
  useEffect(() => {
    const neu = () => { recompute(); };
    window.addEventListener('cui-gruppenraum-gelesen', neu);
    return () => window.removeEventListener('cui-gruppenraum-gelesen', neu);
  }, [recompute]);

  useLiveChannel({
    label: 'PartnerCounts',
    onResync: recompute,
    onMessage: (msg: any) => {
      if (typeof msg.type === 'string' && msg.type.startsWith('partner-')) recompute();
    },
  });

  return counts;
}
